import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, Scale, User, Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Wordmark } from "./Wordmark";
import { useShop } from "../store/ShopContext";
import { CATEGORY_NAMES } from "../lib/api";

const NAV = [
  { label: "Cărucioare", to: "/catalog/carucioare", mega: true },
  { label: "Sport & All-Terrain", to: "/catalog/sport" },
  { label: "Multisport", to: "/catalog/multisport" },
  { label: "Scaune auto", to: "/catalog/scaune-auto" },
  { label: "Despre", to: "/despre" },
];

const MEGA = [
  { slug: "carucioare", desc: "Sleek, Spring 2, Charm 2 în 1" },
  { slug: "sport", desc: "Urban Glide 4 și pachetul Urban Glide 3" },
  { slug: "multisport", desc: "Chariot Sport 2 și Cross 2" },
  { slug: "scaune-auto", desc: "Maple, Elm, Palm și baza Alfi" },
  { slug: "purtare", desc: "Rucsac Thule Sapling" },
];

const Counter = ({ n, testId }) =>
  n > 0 ? (
    <span data-testid={testId} className="absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] px-1 rounded-full bg-[#0A0A0A] text-white text-[10px] font-semibold flex items-center justify-center">
      {n}
    </span>
  ) : null;

export const Header = () => {
  const { cartCount, wishlist, compare, setMiniCartOpen, setSearchOpen, user } = useShop();
  const [mega, setMega] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMobile(false);
    setMega(false);
  }, [location.pathname]);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-40 glass border-b transition-[border-color] duration-300 ${scrolled ? "border-black/10" : "border-transparent"}`} onMouseLeave={() => setMega(false)}>
      <div className="bg-[#0A0A0A] text-white text-[11px] tracking-[0.18em] uppercase py-2 px-4 flex items-center justify-center gap-3">
        <span>Boutique online dedicat</span>
        <img src="/thule-logo.png" alt="Thule Sweden" data-testid="header-thule-logo" className="h-3.5 w-auto invert" />
        <span className="hidden sm:inline">· Cărucioare, remorci multisport și scaune auto · Demo de prezentare</span>
      </div>
      <div className="container-cc h-[72px] flex items-center justify-between gap-6">
        <div className="flex items-center gap-3 lg:hidden">
          <button data-testid="mobile-menu-btn" aria-label="Meniu" className="icon-btn" onClick={() => setMobile(true)}>
            <Menu size={20} strokeWidth={1.5} />
          </button>
        </div>
        <Wordmark />
        <nav className="hidden lg:flex items-center gap-9 h-full">
          {NAV.map((item) => (
            <div key={item.to} className="h-full flex items-center" onMouseEnter={() => setMega(!!item.mega)}>
              <NavLink
                to={item.to}
                data-testid={`nav-${item.to.split("/").pop()}`}
                className={({ isActive }) => `text-[12.5px] uppercase tracking-[0.18em] font-semibold link-underline py-2 ${isActive ? "text-[#0A0A0A]" : "text-[#4A4A4A] hover:text-[#0A0A0A]"}`}
              >
                {item.label}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="flex items-center gap-0.5 sm:gap-1">
          <button data-testid="search-open-btn" aria-label="Caută" className="icon-btn" onClick={() => setSearchOpen(true)}>
            <Search size={20} strokeWidth={1.5} />
          </button>
          <Link data-testid="header-compare-link" aria-label="Comparare" to="/comparare" className="icon-btn hidden sm:inline-flex">
            <Scale size={20} strokeWidth={1.5} />
            <Counter n={compare.length} testId="compare-count" />
          </Link>
          <Link data-testid="header-wishlist-link" aria-label="Favorite" to="/favorite" className="icon-btn">
            <Heart size={20} strokeWidth={1.5} />
            <Counter n={wishlist.length} testId="wishlist-count" />
          </Link>
          <Link data-testid="header-account-link" aria-label="Cont" to="/cont" className="icon-btn hidden sm:inline-flex">
            <User size={20} strokeWidth={1.5} />
            {user && <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#B4A38E]" />}
          </Link>
          <button data-testid="mini-cart-open-btn" aria-label="Coș" className="icon-btn" onClick={() => setMiniCartOpen(true)}>
            <ShoppingBag size={20} strokeWidth={1.5} />
            <Counter n={cartCount} testId="cart-count" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mega && (
          <motion.div
            data-testid="mega-menu"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 top-full bg-white border-b border-black/10 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)] hidden lg:block"
          >
            <div className="container-cc grid grid-cols-12 gap-10 py-10">
              <div className="col-span-3">
                <p className="eyebrow mb-4">Colecții THULE</p>
                <ul className="space-y-3">
                  {MEGA.map((m) => (
                    <li key={m.slug}>
                      <Link to={`/catalog/${m.slug}`} data-testid={`mega-link-${m.slug}`} className="group block">
                        <span className="font-serif text-2xl leading-none group-hover:text-[#B4A38E] transition-colors duration-200">{CATEGORY_NAMES[m.slug]}</span>
                        <span className="block text-xs text-[#737373] mt-1">{m.desc}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-span-3 border-l border-black/10 pl-10">
                <p className="eyebrow mb-4">După etapă</p>
                <ul className="space-y-2.5 text-sm">
                  <li><Link className="link-underline" to="/catalog?stage=newborn">Nou-născut (0–6 luni)</Link></li>
                  <li><Link className="link-underline" to="/catalog?stage=toddler">6 luni – 3 ani</Link></li>
                  <li><Link className="link-underline" to="/catalog?children=2">Doi copii</Link></li>
                  <li><Link className="link-underline" to="/catalog?activity=Jogging">Jogging & activ</Link></li>
                  <li><Link className="link-underline" to="/catalog/scaune-auto">Siguranță în mașină</Link></li>
                </ul>
              </div>
              <Link to="/produs/set-carucior-thule-sleek-2-landou-black" className="col-span-3 group">
                <div className="surface aspect-[4/3] flex items-center justify-center p-6 overflow-hidden">
                  <img src="https://aleo.ro/media/catalog/product/cache/e12079e20fd1e2b418c0a812acc403bd/f/9/f98bc181915ecbb86cdac8d0a9b6610475c3639410accf0583793ec93ecb4d81.jpeg" alt="Set Thule Sleek 2 + Landou" className="product-img max-h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="eyebrow mt-3">Set complet</p>
                <p className="font-serif text-xl">Thule Sleek 2 + Landou</p>
              </Link>
              <Link to="/produs/carucior-thule-urban-glide-4-all-terrain-black" className="col-span-3 group">
                <div className="surface aspect-[4/3] flex items-center justify-center p-6 overflow-hidden">
                  <img src="https://aleo.ro/media/catalog/product/cache/e12079e20fd1e2b418c0a812acc403bd/t/a/ta10101976_1.jpg" alt="Thule Urban Glide 4" className="product-img max-h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                </div>
                <p className="eyebrow mt-3">Nou</p>
                <p className="font-serif text-xl">Thule Urban Glide 4</p>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {createPortal(
      <AnimatePresence>
        {mobile && (
          <>
            <motion.div className="fixed inset-0 bg-black/40 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobile(false)} />
            <motion.aside
              data-testid="mobile-menu"
              className="fixed inset-y-0 left-0 w-[86vw] max-w-sm bg-white z-50 lg:hidden flex flex-col"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex items-center justify-between h-[72px] px-5 border-b border-black/10">
                <Wordmark />
                <button data-testid="mobile-menu-close" aria-label="Închide" className="icon-btn" onClick={() => setMobile(false)}>
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-5 py-6">
                <p className="eyebrow mb-4">Colecții</p>
                {MEGA.map((m) => (
                  <Link key={m.slug} to={`/catalog/${m.slug}`} data-testid={`mobile-link-${m.slug}`} className="flex items-center justify-between py-3.5 border-b border-black/5">
                    <span className="font-serif text-2xl">{CATEGORY_NAMES[m.slug]}</span>
                    <ArrowRight size={16} strokeWidth={1.5} />
                  </Link>
                ))}
                <div className="mt-8 space-y-3 text-sm">
                  <Link to="/comparare" className="block">Comparare produse</Link>
                  <Link to="/cont" className="block">Contul meu</Link>
                  <Link to="/despre" className="block">Despre Cassia Concept</Link>
                  <Link to="/contact" className="block">Contact</Link>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>,
      document.body)}
    </header>
  );
};
